import React, {Component} from 'react';
import './ChangePasswordComponent.less';
import {LoadingOverlay} from '@homeaway/react-loading-overlay';
import {InlineMessage} from '@homeaway/react-alerts';
import {ButtonPrimary} from '@homeaway/react-buttons';
import {validatePasswordConstraint, pwdLengthOptions, validate} from '../../util/password.settings';
import {TrackingContext} from '@homeaway/react-event-tracking';
import aamClient from '../../util/aamClient';

class ChangePasswordComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            password: '',
            confirmPassword: '',
            ticketId: this.props.ticketId,
            loggedIn: false,
            loading: false,
            passwordType: 'password',
            confirmPasswordType: 'password',
            pwdReqMet: false
        };
    }

    componentDidMount = () => {
        this.context.trigger('generic.event', {eventaction: 'changePassword.visible', eventcategory: 'lex', eventlabel: 'ums'});
    }

    showHide = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const {passwordType, confirmPasswordType} = this.state;
        let control = e.target.id;
        const shouldHide =
            control === 'passwordType'
                ? passwordType === 'input'
                : confirmPasswordType === 'input';
        const action = shouldHide ? 'hide' : 'show';
        const eventaction = control === 'passwordType' ? `${action}Pwd.click` : `${action}ConfirmPwd.click`;
        this.context.trigger('generic.click', {eventcategory: 'lex', eventaction});
        this.setState({
            [control]: shouldHide ? 'password' : 'input'
        });
    };

    handleSubmit = (event) => {
        event.stopPropagation();
        event.preventDefault();
    }

    handleChange = (event) => {
        this.setState({[event.target.name]: event.target.value});
    };

    handleError = (error, statusCode) => {
        const {handleError} = this.props;
        this.context.trigger('generic.event', {
            eventaction: 'passwordreset.failure',
            eventcategory: 'passwordreset',
            eventvalue: `${statusCode}`,
            eventlabel: 'ums'});
        handleError(error);
        this.setState({confirmPassword: '', password: ''});
    };

    isValidPassword = () => !validate({p: this.state.password}, validatePasswordConstraint);

    passwordsMatch() {
        const {confirmPassword, password} = this.state;
        return confirmPassword === password;
    }

    validateFields = () => {
        const {confirmPassword, password} = this.state;
        return !(password === '' || confirmPassword === '');
    };

    triggerSubmit = (e) => {
        e.preventDefault();
        this.props.enableLoading();
        setTimeout(() => {
            this.submitForm();
        }, 800);
    };

    submitForm = () => {
        const {disableLoading} = this.props;
        if (!this.isValidPassword() || !this.passwordsMatch()) {
            disableLoading();
            return;
        }
        this.context.trigger('generic.event', {eventaction: 'passwordreset.attempt', eventcategory: 'passwordreset', eventlabel: 'ums'});
        const payload = {
            newPassword: this.state.password,
            confirmedPassword: this.state.confirmPassword,
            ticketId: this.state.ticketId
        };

        aamClient.passwordChange(payload).then(this.checkStatus);
    };

    checkStatus = (response) => {
        const {disableLoading, lingua} = this.props;
        switch (response.status) {
            case 200:
                response.json().then((json) => {
                    this.changeSuccess(json);
                });
                break;
            case 400: // passwords don't match or invalid or bad format shouldn't happen since we validate UI
                disableLoading();
                this.handleError(lingua.read('lex.password.invalid'), response.status);
                break;
            case 401: // ticket id was invalid
                disableLoading();
                this.handleError(lingua.read('lex.ticket.invalid'), response.status);
                break;
            case 417:
                disableLoading();
                this.handleError(lingua.read('lex.password.previous'), response.status);
                break;
            case 422: // password length too long/short shouldn't happen since we validate UI
                disableLoading();
                this.handleError(
                    lingua.read(
                        'ums.validation.password.length',
                        pwdLengthOptions,
                        'validation'
                    ), response.status
                );
                break;
            default:
                disableLoading();
                this.handleError(lingua.read('error.generic'), response.status);
                break;
        }
    };

    changeSuccess = (json) => {
        const {updatePage} = this.props;
        this.context.trigger('generic.event', {
            eventaction: 'passwordreset.success',
            eventcategory: 'passwordreset',
            eventlabel: 'ums'
        });
        this.context.trigger('login.attempt', {loginsource: 'lex', logintype: 'ums'});
        this.context.trigger('login.success', {loginsource: 'lex', logintype: 'ums', publicuuid: json.publicUuid});
        if (this.props.isLegacy === true) { // if user was legacy promoted we need to follow different flow
            updatePage('checkSecurity');
        } else {
            updatePage('success');
        }
    };

    getPasswordRequirement = (lingua, type) => (
        <InlineMessage
            id="pwdMessage"
            type={type}
            message={lingua.read('lex.password.requirements', pwdLengthOptions)}
        />
    );

    getPasswordContent = () => {
        const {lingua} = this.props;
        const {password} = this.state;
        let passwordFieldClasses = `form-group floating-label ${
            this.state.password === '' ? '' : 'not-empty'
        } has-feedback`;
        const promptTxt = lingua.read('lex.password.change.prompt');
        return (
            <div className={passwordFieldClasses}>
                <label>{promptTxt}</label>
                <input
                    type={this.state.passwordType}
                    className="form-control"
                    aria-label={promptTxt}
                    name="password"
                    onChange={this.handleChange}
                    value={this.state.password}
                    autoComplete="new-password"
                />
                <span
                    id="passwordType"
                    className="PasswordShow"
                    onClick={this.showHide}
                >
                    {lingua.read(
                        `lex.password.${
                            this.state.passwordType === 'input'
                                ? 'hide'
                                : 'show'
                        }`
                    )}
                </span>
                {password === '' && this.getPasswordRequirement(lingua, 'help')}
                {password !== '' &&
                    this.getPasswordRequirement(
                        lingua,
                        this.isValidPassword() ? 'success' : 'danger'
                    )}
            </div>
        );
    };

    triggerEdapEvent = () => {
        if (!this.passwordsMatch()) {
            this.context.trigger('generic.event', {eventaction: 'changePassword.mismatch', eventcategory: 'lex', eventlabel: 'ums'});
        }
    }

    getConfirmPwdContent = () => {
        let {lingua} = this.props;
        let {confirmPassword, confirmPasswordType} = this.state;
        let confirmPasswordFieldClasses = `form-group floating-label ${
            confirmPassword === '' ? '' : 'not-empty'
        } has-feedback`;
        let confirmTxt = lingua.read('lex.password.confirm');
        return (
            <div className={confirmPasswordFieldClasses}>
                <label>{confirmTxt}</label>
                <input
                    type={confirmPasswordType}
                    className="form-control"
                    aria-label={confirmTxt}
                    name="confirmPassword"
                    onChange={this.handleChange}
                    value={this.state.confirmPassword}
                    autoComplete="confirm-password"
                    onBlur={this.triggerEdapEvent}
                />
                <span
                    id="confirmPasswordType"
                    className="PasswordShow"
                    onClick={this.showHide}
                >
                    {lingua.read(
                        `lex.password.${
                            confirmPasswordType === 'input' ? 'hide' : 'show'
                        }`
                    )}
                </span>
                {confirmPassword !== '' && (
                    <InlineMessage
                        id="confirmPwdMessage"
                        type={this.passwordsMatch() ? 'success' : 'danger'}
                        message={lingua.read(
                            `lex.password.change.confirm${
                                this.passwordsMatch() ? '' : '.error'
                            }`
                        )}
                    />
                )}
            </div>
        );
    };

    render = () => {
        let {lingua, isWaiting} = this.props;
        let passwordContent = this.getPasswordContent();
        let confirmPwdContent = this.getConfirmPwdContent();

        return (
            <div className="ChangePasswordComponent">
                <form onSubmit={this.triggerSubmit} noValidate>
                    {passwordContent}
                    {confirmPwdContent}
                    <div className="Save_button text-center">
                        <ButtonPrimary
                            type="submit"
                            label={lingua.read('lex.password.change.button')}
                            disabled={!this.validateFields()}
                        >
                            <LoadingOverlay loading={isWaiting} />
                        </ButtonPrimary>
                    </div>
                </form>
            </div>
        );
    };
}

ChangePasswordComponent.contextType = TrackingContext;
export default ChangePasswordComponent;
